/* eslint-disable react-hooks/exhaustive-deps */
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import * as Constants from '../../constants';
import * as Utils from '../../utils';

import './VisibilityLogger.scss';

const defaultOffsets = {
  top: Constants.Styling.GlobalStyleConstants.TOP_BAR_HEIGHT,
  right: 0,
  bottom: Constants.Styling.GlobalStyleConstants.TUNER_BAR_HEIGHT,
  left: 0,
};

export function isComponentInView(element, visibilitySettings) {
  const { staticHeight, offsets } = visibilitySettings;
  const viewportOffsets = { ...defaultOffsets, ...offsets };
  const isElementInViewport = Utils.PageVisibility.isElementInViewport(
    element,
    viewportOffsets,
    staticHeight,
  );
  const isHidden = Utils.PageVisibility.isHidden();
  return !isHidden && isElementInViewport;
}

export function mapStateToProps(state) {
  return {
    scrolledOffset: Utils.Scroll.getScrolledOffset(state),
    currentPathname: Utils.Route.getCurrentPathnameFromState(state),
  };
}

/**
 * Calls the `viewed` integration action or onComponentInView (if provided) when a React component enters the viewport.
 *
 * Example Usage:
 * export const PodcastProgramRowGridWithLogger = D.withVisibilityLogger(
 *   { contentSection: D.Constants.ContentSections.BackstageContentSections.SIMILAR },
 *   { staticHeight: D.RowItemHeights.DEFAULT, useMemo: true }
 * )(PodcastProgramRowGrid);
 *
 * export default PodcastProgramRowGrid;
 *
 * @param {object} visibilityProps - Any data that would be passed into integration.action.viewed or onComponentInView
 * @param {object} visibilitySettings - Options to define viewport boundaries (offsets), component height (staticHeight), and memoization (useMemo)
 */
export function withVisibilityLogger(visibilityProps = {}, visibilitySettings = {}) {
  return WrappedComponent => {
    const Container = props => <WrappedComponent {...props} />;

    // With the react-redux 7.1.0 upgrade, connected components are already memoized, so we could
    // selectively memoize the WrappedComponent
    const VisibilityLoggerChild = visibilitySettings.useMemo ? React.memo(Container) : Container;

    const VisibilityLogger = props => {
      const containerRef = useRef(null);
      const [viewed, setViewed] = useState(false);
      const { integration } = useContext(Utils.Context.AppContext);
      const { scrolledOffset, currentPathname, onComponentInView, ...componentProps } = props;

      useEffect(() => {
        if (!viewed) {
          const isInView = isComponentInView(containerRef.current, visibilitySettings);

          if (isInView) {
            // For most cases, the view action would only need static data but if instance data is needed,
            // the component could still pass onComponentInView.
            if (isFunction(onComponentInView)) {
              onComponentInView(visibilityProps);
            } else {
              integration.action.viewed(visibilityProps);
            }

            setViewed(true);
          }
        }
      }, [scrolledOffset]);

      useEffect(() => {
        if (viewed) {
          setViewed(false);
        }
      }, [currentPathname]);

      return (
        <div className='VisibilityLogger' ref={containerRef}>
          <VisibilityLoggerChild visibilityKey={currentPathname} {...componentProps} />
        </div>
      );
    };

    VisibilityLogger.propTypes = {
      scrolledOffset: PropTypes.number.isRequired,
      currentPathname: PropTypes.string.isRequired,
      onComponentInView: PropTypes.func,
    };

    return connect(mapStateToProps)(VisibilityLogger);
  };
}
